/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { useSuspenseQuery } from '@apollo/client';

import * as Types from '@xing-com/crate-common-graphql-types';
import type { MasterDirectoryEntryType } from '@xing-com/crate-common-graphql-types';
import { getXingJobsDirectoryLetterJobRolePath } from '@xing-com/crate-core-routes';
import {
  MasterDirectoryEntryByLetterDocument,
  MasterDirectoryOtherEntriesDocument,
} from '@xing-com/crate-master-directory-graphql';

type EntryByLetterParams = {
  letter: string;
  queryKey?: number;
  type: MasterDirectoryEntryType;
};

export const useGetEntriesByLetter = ({
  letter,
  queryKey,
  type,
}: EntryByLetterParams): {
  collection: {
    displayName: string;
    url: string;
    moreUrl?: string;
  }[];
} => {
  const document =
    letter === 'weitere'
      ? MasterDirectoryOtherEntriesDocument
      : MasterDirectoryEntryByLetterDocument;
  const { data } = useSuspenseQuery(document, {
    queryKey,
    variables: {
      letter,
      type,
    },
  });
  let collection: {
    displayName: string;
    url: string;
    moreUrl?: string;
  }[] = [];

  if (letter === 'weitere' && data) {
    // Data is of type MasterDirectoryOtherEntriesQuery
    (Object.keys(data) as Array<keyof typeof data>).forEach((key) => {
      collection.push(
        ...(
          (data[key] as Types.MasterDirectoryEntryList)?.collection || []
        ).map((entry) => ({
          displayName: entry?.displayName || '',
          url: entry?.url || '',
        }))
      );
    });
  } else if (letter !== 'weitere' && data) {
    // Data is of type MasterDirectoryEntryByLetterQuery
    const byLetterData = data as Types.Query;
    collection = (
      byLetterData.masterDirectoryEntryByLetter?.collection || []
    ).map((entry) => ({
      displayName: entry?.displayName || '',
      url: entry?.url || '',
    }));
  }

  collection = collection
    .filter((entry): entry is Types.MasterDirectoryEntry => entry !== null)
    .map((entry) => {
      let displayName = entry.displayName || '';

      if (
        type === Types.MasterDirectoryEntryType.JobRole ||
        type === Types.MasterDirectoryEntryType.Company
      ) {
        displayName = `${displayName} Jobs`;
      }
      if (type === Types.MasterDirectoryEntryType.Location) {
        displayName = `Jobs in ${displayName}`;
      }

      return {
        ...entry,
        displayName,
        url: entry.url || '',
        moreUrl: getXingJobsDirectoryLetterJobRolePath({
          letter,
          jobRole: entry.displayName || '',
        })
          .replace(/ /g, '-')
          .toLowerCase(),
      };
    });

  return { collection };
};
